'use client'

import * as React from 'react'
import { createPortal } from 'react-dom'
import { usePathname, useRouter } from 'next/navigation'
import { Search, SlidersHorizontal, X, Loader2, Clock, Trash2, Filter, ChevronDown, ChevronUp, Plus, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSearchStore, useUserStore } from '@/lib/store'
import { useAuth } from '@/components/auth'
import { removeRecentSearch as removeRecentSearchFirestore, clearRecentSearches as clearRecentSearchesFirestore } from '@/lib/firebase'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui'
import { RecentSearch, TransactionType } from '@/lib/types'
import { FilterModal } from './filter-modal'
import { useFeatureGroups } from './use-feature-groups'
import {
  DualRange,
  RoomsRange,
  SALE_PRICE_MAX,
  SALE_PRICE_STEP,
  fmtK,
  linearScale,
  saleScale,
  parseAreaInput,
  parsePriceInput,
} from './range-controls'
import { Seg } from '@/components/roostr'
import { ROOMS_MAX, PRICE_MIN, PRICE_MAX, PRICE_STEP, AREA_MIN, AREA_MAX, AREA_STEP } from '@/lib/constants'
import { useDictation, DictationMicButton } from './dictation'
import { QUERY_CHIPS, escapeRegExp } from './search-input'
import { useTypewriter } from '@/lib/useTypewriter'
import { useI18n } from '@/lib/i18n'

interface MobileSearchSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSearch: () => void
  /** Gate mode: quick query chips instead of the Filters button, matching
      the desktop gate (which hides FilterTags). */
  showQueryChips?: boolean
}

const EXAMPLE_QUERIES = [
  "2 rooms, under 25k, Praha 3",
  "1+ room, under 10k total",
  "20 min from Anděl metro, furnished",
  "2 rooms, balcony",
  "3+ rooms, parking",
  "in Praha 7",
  "15 min from Strossmayerovo náměstí",
  "near Florenc, furnished",
  "in Praha 12, pet friendly",
  "2+1, balcony, under 20k",
  "dishwasher, washing machine",
  "with desk",
  "bathtub, under 25k",
  "bright, balcony",
]

// Rooms and price are owned by the RoomsRange / DualRange controls (the same
// ones the desktop filters use), which write store filters. These two QUERY
// chips are therefore still excluded: as query TEXT they would be a second,
// competing writer for criteria the sliders already express precisely.
// (`search.chips.*` room/price keys are retained in i18n — the landing gate
// still uses that vocabulary.)
const SUPERSEDED_CHIP_KEYS = new Set(['gate.chips.rooms2', 'gate.chips.under25k'])

// One toggleable query chip — shared by the rooms/price rows, the full
// catalogue rows, and the fallback features row.
function SheetChip({
  phrase,
  active,
  onClick,
}: {
  phrase: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        // Always single-line. The rooms/price rows still scroll horizontally,
        // and in the wrapping catalogue rows flex-shrink-0 is what stops a
        // label wrapping INSIDE its own pill instead of moving to the next row.
        'inline-flex flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-[11px] py-[5px] text-[13px] transition-colors',
        active
          ? 'border border-primary/35 bg-primary/10 font-medium text-primary'
          : 'border border-border/70 bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
    >
      {active ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
      {phrase}
    </button>
  )
}

export function MobileSearchSheet({ open, onOpenChange, onSearch, showQueryChips = false }: MobileSearchSheetProps) {
  const { t } = useI18n()
  const { query, setQuery, isLoading, filters, updateFilter, activeTags, selectedCity, selectedDistricts, setFilters, clearFilters, clearTags, addTag, setSelectedCity, setSelectedDistricts, setTravelTime, setSort } = useSearchStore()
  const { recentSearches, removeRecentSearch, clearRecentSearches } = useUserStore()
  const { user } = useAuth()
  const pathname = usePathname()
  const router = useRouter()
  
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [mounted, setMounted] = React.useState(false)
  const [localQuery, setLocalQuery] = React.useState(query)
  const [filterModalOpen, setFilterModalOpen] = React.useState(false)
  const [showExamples, setShowExamples] = React.useState(false)
  const [isFocused, setIsFocused] = React.useState(false)

  // Typewriter animation for placeholder
  const animatedPlaceholder = useTypewriter(EXAMPLE_QUERIES, {
    typingSpeed: 80,
    deletingSpeed: 40,
    pauseDuration: 2500,
    paused: isFocused || localQuery.length > 0,
  })

  // Sync local query with store when modal opens
  React.useEffect(() => {
    if (open) {
      setLocalQuery(query)
      // Don't auto-focus the input so the typewriter placeholder can animate.
      // User taps the input when ready to type.
    }
  }, [open, query])

  // Mount check for portal
  React.useEffect(() => {
    setMounted(true)
  }, [])

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  // Close on escape
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onOpenChange(false)
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [open, onOpenChange])

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault()
    setQuery(localQuery)
    onOpenChange(false)
    // Use setTimeout to ensure the store state has updated before triggering search
    // This fixes the closure issue where handleSearch captures stale query value
    setTimeout(() => {
      onSearch()
    }, 0)
  }

  const handleClear = () => {
    setLocalQuery('')
    setIsFocused(true)
    inputRef.current?.focus()
  }

  const handleExampleClick = (example: string) => {
    setLocalQuery(example)
    setIsFocused(true)
    inputRef.current?.focus()
  }

  // Query-chip toggle on the sheet's local draft (mirrors SearchInput's):
  // active = phrase present in the text; removal tidies the comma joins.
  const chipActive = (phrase: string) => localQuery.toLowerCase().includes(phrase.toLowerCase())
  const toggleChip = (phrase: string) => {
    if (chipActive(phrase)) {
      setLocalQuery(
        localQuery
          .replace(new RegExp(`(,\\s*)?${escapeRegExp(phrase)}`, 'i'), '')
          .replace(/^\s*,\s*/, '')
          .replace(/\s{2,}/g, ' ')
          .trim()
      )
    } else {
      setLocalQuery(localQuery.trim() ? `${localQuery.replace(/[,\s]+$/, '')}, ${phrase}` : phrase)
    }
    inputRef.current?.focus()
  }

  // Composer rows (translated once per render — chip phrases ARE query text).
  const featureChips = QUERY_CHIPS.filter(({ key }) => !SUPERSEDED_CHIP_KEYS.has(key)).map(
    ({ key, fallback }) => t(key, fallback)
  )

  // ── Rooms / price: the SAME controls the desktop filters use (user-chosen
  // 2026-08-02, replacing the old "1 room"/"under 25k" preset chips). These
  // write STORE FILTERS, not query text — which is exactly why the presets had
  // to go: a typed "under 25k" and a slider at 40k would have been two writers
  // for one criterion. Derivation mirrors FilterModal's verbatim so both
  // surfaces read and write identical semantics (undefined at the extremes =
  // "Any").
  const isSale = filters.transaction_type === 'sale'
  const priceMax = isSale ? SALE_PRICE_MAX : PRICE_MAX
  const priceStep = isSale ? SALE_PRICE_STEP : PRICE_STEP

  const roomsLo = filters.rooms_min ?? (filters.rooms_max != null ? 1 : null)
  const roomsHi = roomsLo == null ? null : Math.min(filters.rooms_max ?? ROOMS_MAX, ROOMS_MAX)
  const handleRoomsChange = ([lo, hi]: [number | null, number | null]) => {
    updateFilter('rooms_min', lo ?? undefined)
    updateFilter('rooms_max', hi == null || hi >= ROOMS_MAX ? undefined : hi)
  }

  const priceValue: [number, number] = [filters.price_min ?? PRICE_MIN, filters.price_max ?? priceMax]
  const handlePriceChange = ([lo, hi]: [number, number]) => {
    updateFilter('price_min', lo === PRICE_MIN ? undefined : lo)
    updateFilter('price_max', hi === priceMax ? undefined : hi)
  }
  const formatPrice = (v: number) =>
    v === 0 ? '0 Kč' : v >= priceMax ? t('search.any', 'Any') : `${fmtK(v, isSale)} Kč`

  const areaValue: [number, number] = [filters.area_min ?? AREA_MIN, filters.area_max ?? AREA_MAX]
  const handleAreaChange = ([lo, hi]: [number, number]) => {
    updateFilter('area_min', lo === AREA_MIN ? undefined : lo)
    updateFilter('area_max', hi === AREA_MAX ? undefined : hi)
  }
  const formatArea = (v: number) => (v >= AREA_MAX ? t('search.any', 'Any') : `${v} m²`)

  // Rent↔sale flips the whole price domain (80k vs 20M), so a bound carried
  // across would be nonsense — FilterModal clears them on the same transition.
  const prevIsSaleRef = React.useRef(isSale)
  React.useEffect(() => {
    if (prevIsSaleRef.current === isSale) return
    prevIsSaleRef.current = isSale
    if (filters.price_min != null) updateFilter('price_min', undefined)
    if (filters.price_max != null) updateFilter('price_max', undefined)
  }, [isSale, filters.price_min, filters.price_max, updateFilter])
  // The FULL catalogue, same groups FilterModal renders (shared hook) —
  // "all available filters" clickable from the sheet. Labels are localized
  // catalogue labels, which are exactly the parser's keyword aliases.
  const featureGroups = useFeatureGroups()

  // ── Voice dictation — shared engine (see dictation.tsx). Appends into
  // the sheet's local draft; the sheet additionally stops the mic whenever
  // it closes.
  const dictation = useDictation(() => localQuery, setLocalQuery)
  const { stop: stopDictation } = dictation
  React.useEffect(() => {
    if (!open) stopDictation()
  }, [open, stopDictation])

  const handleClose = () => {
    onOpenChange(false)
  }

  const handleSelectRecentSearch = (search: RecentSearch) => {
    if (search.type === 'text' && search.query) {
      setQuery(search.query)
      setLocalQuery(search.query)
      clearFilters()
    } else {
      setQuery('')
      setLocalQuery('')
      if (search.filters) setFilters({ ...search.filters })
      clearTags()
      search.tags?.forEach((tag) => addTag(tag))
    }
    if (search.city) setSelectedCity(search.city)
    if (search.districts) setSelectedDistricts(search.districts)
    if (search.travelTime !== undefined) setTravelTime(search.travelTime)
    if (search.sort) setSort(search.sort)
    
    onOpenChange(false)
    
    // If we're on the search page, dispatch event to trigger search
    // Otherwise, navigate to search page which will auto-trigger search on mount
    if (pathname === '/search') {
      const source = search.type === 'filters' ? 'filters' : 'text'
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('header-search-triggered', { detail: { source } }))
      }, 0)
    } else {
      // Navigate to search page - it will auto-trigger search on mount with the updated state
      router.push('/search')
    }
  }

  const handleRemoveRecentSearch = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    removeRecentSearch(id)
    if (user) {
      removeRecentSearchFirestore(user.uid, id).catch(console.error)
    }
  }

  const handleClearAllRecentSearches = () => {
    clearRecentSearches()
    if (user) {
      clearRecentSearchesFirestore(user.uid).catch(console.error)
    }
  }

  // Count active filters for badge
  const locationFilterCount =
    (filters.type && filters.type !== 'apartment' ? 1 : 0) +
    (filters.transaction_type && filters.transaction_type !== 'rent' ? 1 : 0) +
    (selectedCity && selectedCity !== 'Praha' ? 1 : 0) +
    (selectedDistricts.length > 0 ? 1 : 0)

  const numericFilterCount =
    (filters.rooms_min ? 1 : 0) + (filters.rooms_max ? 1 : 0) +
    (filters.area_min ? 1 : 0) + (filters.area_max ? 1 : 0) +
    (filters.price_min ? 1 : 0) + (filters.price_max ? 1 : 0)

  const totalActiveFilters = activeTags.length + numericFilterCount + locationFilterCount

  // Filter recent searches by typed text
  const isFiltering = !!localQuery.trim()
  const filteredSearches = React.useMemo(() => {
    if (!isFiltering) return recentSearches.slice(0, 8)
    const needle = localQuery.trim().toLowerCase()
    return recentSearches.filter((s) => s.displayLabel.toLowerCase().includes(needle)).slice(0, 8)
  }, [recentSearches, localQuery, isFiltering])

  if (!mounted || !open) return null

  const modalContent = (
    // Full-screen takeover (was a centered max-w-md dialog): with the iOS
    // keyboard open a centered modal is half-occluded, and the chip composer
    // needs the room. Body scrolls; the action bar is pinned with safe-area
    // padding (same recipe as FilterModal's mobile footer).
    <div className="fixed inset-0 z-[100] flex flex-col bg-background animate-in slide-in-from-bottom-2 fade-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold">{t('search.searchButton', 'Search')}</h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 -mr-2 rounded-full hover:bg-muted transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content — scrollable; the submit row is pinned below. */}
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={localQuery}
              onChange={(e) => setLocalQuery(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder={animatedPlaceholder}
              className={cn(
                'w-full h-12 pl-12 rounded-xl',
                // Right padding clears the [× clear][mic] stack when the mic
                // renders (voice-capable browsers), just the × otherwise.
                dictation.supported ? 'pr-20' : 'pr-12',
                // Solid card surface + 2px border — matches the desktop
                // SearchInput so the sheet reads as the same control.
                'bg-card border-2 border-border',
                'text-base text-foreground placeholder:text-muted-foreground',
                'focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20',
                'transition-all duration-200'
              )}
              disabled={isLoading}
            />
            <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-0.5">
              {localQuery && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="p-1.5 rounded-full hover:bg-muted transition-colors"
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              )}
              {dictation.supported && (
                <DictationMicButton listening={dictation.listening} onToggle={dictation.toggle} className="p-2" />
              )}
            </div>
          </div>

          {/* Recent searches — directly under the input (one tap to re-run;
              was buried below the chip groups). Horizontal row; typing
              filters it (Matching searches). */}
          {user && recentSearches.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  {isFiltering ? (
                    <>
                      <Filter className="w-3.5 h-3.5" />
                      {t('search.matchingSearches', 'Matching searches')}
                    </>
                  ) : (
                    <>
                      <Clock className="w-3.5 h-3.5" />
                      {t('search.recentSearches', 'Recent searches')}
                    </>
                  )}
                </div>
                {!isFiltering && (
                  <button
                    type="button"
                    onClick={handleClearAllRecentSearches}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                    {t('search.clearAll', 'Clear all')}
                  </button>
                )}
              </div>
              {filteredSearches.length > 0 ? (
                <div className="-mx-4 flex flex-nowrap gap-2 overflow-x-auto scrollbar-hide px-4">
                  {filteredSearches.map((search) => (
                    <button
                      key={search.id}
                      type="button"
                      onClick={() => handleSelectRecentSearch(search)}
                      className="group flex-shrink-0 max-w-[75vw]"
                    >
                      <Badge
                        variant="secondary"
                        className={cn(
                          'text-sm font-normal px-3 py-2 gap-1.5 cursor-pointer max-w-full',
                          'active:bg-primary/15 active:text-primary',
                          'transition-colors',
                        )}
                      >
                        {search.type === 'text' ? (
                          <Search className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                        ) : (
                          <SlidersHorizontal className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                        )}
                        <span className="truncate">{search.displayLabel}</span>
                        <span
                          role="button"
                          tabIndex={-1}
                          onClick={(e) => handleRemoveRecentSearch(e, search.id)}
                          className="ml-0.5 shrink-0 rounded-full p-0.5 hover:bg-muted-foreground/20 transition-all"
                        >
                          <X className="w-3 h-3" />
                        </span>
                      </Badge>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground/60 py-1">
                  {t('search.noMatchingSearches', 'No matching searches')}
                </p>
              )}
            </div>
          )}

          {/* Transaction type selector */}
          <div className="flex gap-2">
            {(['rent', 'sale'] as TransactionType[]).map((transactionType) => (
              <button
                key={transactionType}
                type="button"
                onClick={() => updateFilter('transaction_type', transactionType)}
                className={cn(
                  'flex-1 py-2 rounded-full text-sm font-medium transition-colors',
                  // Soft primary tint like the desktop transaction pill —
                  // the old solid red outshouted everything in the sheet.
                  (filters.transaction_type || 'rent') === transactionType
                    ? 'border border-primary/35 bg-primary/10 text-primary'
                    : 'border border-transparent bg-muted/60 text-muted-foreground hover:bg-muted'
                )}
              >
                {t(`search.${transactionType}`, transactionType)}
              </button>
            ))}
          </div>

          {/* Chip composer — tap to write the query instead of typing (the
              mobile answer to "typing is inconvenient"). Chips edit visible
              NL text in the input, so the parse path stays single and the
              user learns the grammar by watching chips write it. Rooms and
              price are exclusive groups (one dispozice, one budget). */}
          <div className="space-y-3">
            {/* Rooms + price = the desktop filters' own controls, not presets.
                They set store filters directly, so they compose with (rather
                than fight) whatever the query text says. */}
            <div>
              <div className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.8px] text-muted-foreground">
                {t('search.chipGroupRooms', 'Rooms')}
              </div>
              <RoomsRange lo={roomsLo} hi={roomsHi} onChange={handleRoomsChange} />
            </div>

            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-muted-foreground">
                  {t('search.chipGroupPrice', 'Price')}
                </span>
                {/* Rent-only vs incl.-utilities, same store field as desktop.
                    Meaningless for sale prices, so it follows isSale. */}
                {!isSale && (
                  <Seg<'rent' | 'total'>
                    value={filters.use_total_price !== false ? 'total' : 'rent'}
                    onChange={(v) => updateFilter('use_total_price', v === 'total')}
                    options={[
                      { value: 'rent', label: t('filterModal.rentOnly', 'Rent only') },
                      { value: 'total', label: t('filterModal.inclUtilities', 'Incl. utilities') },
                    ]}
                    compact
                    className="flex-none"
                  />
                )}
              </div>
              <DualRange
                min={PRICE_MIN}
                max={priceMax}
                step={priceStep}
                value={priceValue}
                onChange={handlePriceChange}
                format={formatPrice}
                scale={isSale ? saleScale : linearScale}
                inputFormat={(v) => (v >= priceMax ? t('search.any', 'Any') : v === 0 ? '0' : fmtK(v, isSale))}
                inputParse={parsePriceInput}
              />
              {/* The thumbs overhang the 4px track by ~12px; without this the
                  next section's heading sat right under them. */}
              <div className="h-2" />
            </div>

            <div>
              <div className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.8px] text-muted-foreground">
                {t('search.area', 'Area')}
              </div>
              <DualRange
                min={AREA_MIN}
                max={AREA_MAX}
                step={AREA_STEP}
                value={areaValue}
                onChange={handleAreaChange}
                format={formatArea}
                inputFormat={(v) => (v >= AREA_MAX ? t('search.any', 'Any') : String(v))}
                inputParse={parseAreaInput}
              />
              <div className="h-2" />
            </div>

            {/* The FULL feature catalogue — one horizontally scrollable row
                per category, the same groups FilterModal renders (shared
                hook), so every available filter is one tap away here. Chip
                labels are the catalogue's localized keyword labels — exactly
                the parser's aliases, so tapping writes parseable query text. */}
            {featureGroups.length > 0 ? (
              featureGroups.map(({ id, label, Icon, items }) => (
                <div key={id}>
                  <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.8px] text-muted-foreground">
                    <Icon className="h-3.5 w-3.5" />
                    {label}
                  </div>
                  {/* Wraps rather than scrolling sideways. As single-line rows
                      these hid most of their own contents — KITCHEN kept 568px
                      of 8 chips off-screen — inside a sheet that already
                      scrolls vertically, so a whole category read as 3 chips.
                      Wrapping trades height (the axis phones scroll well) for
                      every option being findable. */}
                  <div className="flex flex-wrap gap-2">
                    {items.map(({ id: kwId, label: phrase }) => (
                      <SheetChip
                        key={kwId}
                        phrase={phrase}
                        active={chipActive(phrase)}
                        onClick={() => toggleChip(phrase)}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              /* Catalogue not loaded yet — curated static fallback. */
              <div>
                <div className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.8px] text-muted-foreground">
                  {t('search.chipGroupFeatures', 'Features')}
                </div>
                <div className="-mx-4 flex flex-nowrap gap-2 overflow-x-auto scrollbar-hide px-4">
                  {featureChips.map((phrase) => (
                    <SheetChip
                      key={phrase}
                      phrase={phrase}
                      active={chipActive(phrase)}
                      onClick={() => toggleChip(phrase)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* More examples toggle */}
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => setShowExamples(!showExamples)}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              {showExamples ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  {t('search.hideExamples', 'Hide examples')}
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  {t('search.moreExamples', 'More examples')}
                </>
              )}
            </button>
          </div>

          {/* Example queries */}
          {showExamples && (
            <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto scrollbar-hide">
              {EXAMPLE_QUERIES.map((example, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleExampleClick(example)}
                  className={cn(
                    "px-3 py-1.5 text-sm rounded-full whitespace-nowrap",
                    "bg-muted/50 text-muted-foreground",
                    "hover:bg-primary/10 hover:text-primary active:bg-primary/15",
                    "transition-colors"
                  )}
                >
                  {example}
                </button>
              ))}
            </div>
          )}

          </div>

          {/* Pinned action bar — safe-area padded like FilterModal's mobile
              footer. The gate (showQueryChips) has NO Filters button: chips
              replace it, matching the desktop gate. */}
          <div className="flex gap-2 border-t border-border bg-background p-4 pb-[max(16px,env(safe-area-inset-bottom))]">
            {!showQueryChips && (
              <button
                type="button"
                onClick={() => setFilterModalOpen(true)}
                className={cn(
                  'flex items-center justify-center gap-2 h-11 px-4 rounded-xl',
                  'border-2 transition-all',
                  totalActiveFilters > 0
                    ? 'bg-primary/10 text-primary border-primary'
                    : 'bg-muted/50 text-muted-foreground border-border hover:border-primary/50'
                )}
              >
                <SlidersHorizontal className="h-4 w-4" />
                <span className="text-sm font-medium">{t('search.filters', 'Filters')}</span>
                {totalActiveFilters > 0 && (
                  <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                    {totalActiveFilters}
                  </span>
                )}
              </button>
            )}

            {/* Search Button */}
            <Button
              type="submit"
              disabled={isLoading}
              className="flex-1 h-11"
              size="lg"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('search.searching', 'Searching…')}
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  {t('search.searchButton', 'Search')}
                </>
              )}
            </Button>
          </div>
        </form>

      {/* Filter Modal (not reachable in gate mode — no Filters button) */}
      {!showQueryChips && (
      <FilterModal
        open={filterModalOpen}
        onOpenChange={setFilterModalOpen}
        onApply={() => {
          setFilterModalOpen(false)
          onOpenChange(false)
          // Trigger a 'filters' search so changes made inside the modal (e.g.
          // Rent/Sale) are reflected in results and the URL, matching desktop behavior.
          window.dispatchEvent(new CustomEvent('header-search-triggered', { detail: { source: 'filters' } }))
        }}
      />
      )}
    </div>
  )

  return createPortal(modalContent, document.body)
}
